import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Tab, Nav } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import axios from "axios";
import apiConfig from "../config/apiConfig.js";

import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import EditProfileModal from "./EditProfileModal";
import EditLegacyContactModal from "./EditLegacyContactModal";
import PaymentHistory from "./PaymentHistory";
import GiftLoader from "../../common/GiftLoader";

// New extracted components
import ProfilePhotoUpload from "./ProfilePhotoUpload";
import ProfileContactInfo from "./ProfileContactInfo";
import SubscriptionSection from "./SubscriptionSection";
import LegacyContactsSection from "./LegacyContactsSection";
import MyReferrals from "./MyReferrals";
import ReferFriendsModal from "./ReferFriendsModal";

// Custom hooks
import { useProfileData } from "./hooks/useProfileData";
import { usePaymentHistory } from "./hooks/usePaymentHistory";

// Import phone utilities
import { getRawPhoneDigits } from "../Auth/utils/phoneFormatter.js";

import "./Profile.scss";

const Profile = () => {
  const [activeTab, setActiveTab] = useState("subscription-legacy");
  const navigate = useNavigate();

  // Custom hooks for data management
  const {
    loading,
    userProfile,
    subscriptionInfo,
    legacyContacts,
    paymentMethods,
    updateUserProfile,
    updateLegacyContacts,
    refetchProfileData
  } = useProfileData();

  const {
    paymentHistory,
    paymentHistoryLoading,
    pagination: paymentPagination,
    fetchTransactionHistory
  } = usePaymentHistory();

  // Modal states
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showEditLegacyModal, setShowEditLegacyModal] = useState(false);
  const [selectedLegacyContact, setSelectedLegacyContact] = useState(null);
  const [showReferFriendsModal, setShowReferFriendsModal] = useState(false);

  // Referrals state
  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  // Fetch transaction history when payment-history tab is selected
  useEffect(() => {
    if (activeTab === "payment-history") {
      fetchTransactionHistory();
    }
  }, [activeTab]);

  // Fetch referrals when my-referrals tab is selected
  useEffect(() => {
    if (activeTab === "my-referrals") {
      fetchReferrals();
    }
  }, [activeTab]);

  // Generate initials for profile photo
  const getInitials = () => {
    if (userProfile.firstName && userProfile.lastName) {
      return `${userProfile.firstName
        .charAt(0)
        .toUpperCase()}${userProfile.lastName.charAt(0).toUpperCase()}`;
    } else if (userProfile.fullName) {
      const names = userProfile.fullName.split(" ");
      if (names.length >= 2) {
        return `${names[0].charAt(0).toUpperCase()}${names[1]
          .charAt(0)
          .toUpperCase()}`;
      } else if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
      }
    }
    return "U";
  };

  const handleEditProfile = () => {
    setShowEditProfileModal(true);
  };

  const handleProfilePhotoUpdate = (imageUrl: string) => {
    updateUserProfile({ profileImage: imageUrl });
  };

  const handleEditLegacyContact = (contact = null) => {
    setSelectedLegacyContact(contact);
    setShowEditLegacyModal(true);
  };

  const handleProfileUpdate = async (updatedData) => {
    try {
      const authToken = localStorage.getItem("authToken");

      // Extract raw phone digits (remove formatting)
      const rawPhoneDigits = getRawPhoneDigits(updatedData.phone);

      // Transform data to match backend API expectations
      const apiPayload = {
        firstName: updatedData.firstName,
        lastName: updatedData.lastName,
        phoneNumber: rawPhoneDigits, // Backend expects raw digits only
        address1: updatedData.address1,
        address2: updatedData.address2 || "",
        city: updatedData.city,
        state: updatedData.state,
        zip: updatedData.zip,
      };

      console.log("Sending payload to backend:", apiPayload);

      const response = await axios.put(
        `${apiConfig.apiBaseURL}/update`,
        apiPayload,
        {
          headers: {
            "Content-Type": "application/json",
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );

      // Handle successful response
      if (response.data) {
        // Update local state with the new data
        updateUserProfile({
          firstName: updatedData.firstName,
          lastName: updatedData.lastName,
          phone: updatedData.phone, // Keep formatted version in UI
          address1: updatedData.address1,
          address2: updatedData.address2 || "",
          city: updatedData.city,
          state: updatedData.state,
          zip: updatedData.zip,
          fullName: `${updatedData.firstName} ${updatedData.lastName}`,
        });

        toast.success("Profile updated successfully!");
        setShowEditProfileModal(false);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      if (axios.isAxiosError(error)) {
        console.error("Update failed:", {
          status: error.response?.status,
          data: error.response?.data
        });
      }

      toast.error(error.response?.data?.message || "Failed to update profile. Please try again.");
    }
  };

  const handleLegacyContactUpdate = async (contactData) => {
    try {
      const authToken = localStorage.getItem("authToken");

      // Extract raw phone digits (remove formatting)
      const rawPhoneDigits = getRawPhoneDigits(contactData.phone);

      // Determine if it's primary or secondary contact based on selectedLegacyContact
      const contactType =
        selectedLegacyContact?.id === "primary"
          ? "primary"
          : selectedLegacyContact?.id === "secondary"
          ? "secondary"
          : legacyContacts.length === 0
          ? "primary"
          : "secondary";

      // Match the API structure expected by backend
      const apiPayload = {
        [`${contactType}Contact`]: {
          name: contactData.name,
          email: contactData.email,
          phoneNumber: rawPhoneDigits, // Backend expects raw digits only
          relationship: contactData.relationship,
          address1: contactData.address1,
          address2: contactData.address2 || "",
          city: contactData.city,
          state: contactData.state,
          zip: contactData.zip,
        },
      };

      console.log("Sending legacy contact payload to backend:", apiPayload);

      const response = await axios.put(
        `${apiConfig.apiBaseURL}/update-legacy-contacts`,
        apiPayload,
        {
          headers: {
            "Content-Type": "application/json",
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );


      // Handle successful response
      if (response.data) {

        if (selectedLegacyContact) {
          // Update existing contact
          const updatedContacts = legacyContacts.map((contact) =>
            contact.id === selectedLegacyContact.id
              ? { ...contact, ...contactData, phone: contactData.phone }
              : contact
          );
          updateLegacyContacts(updatedContacts);
          toast.success("Legacy contact updated successfully!");
        } else {
          // Add new contact
          const newContact = {
            id: contactType,
            ...contactData,
            phone: contactData.phone,
          };
          updateLegacyContacts([...legacyContacts, newContact]);
          toast.success(
            `${
              contactType === "primary" ? "Primary" : "Secondary"
            } legacy contact added successfully!`
          );
        }

        setShowEditLegacyModal(false);
        setSelectedLegacyContact(null);

      }
    } catch (error) {
      console.error("Error updating legacy contact:", error);
      if (axios.isAxiosError(error)) {
        console.error("Legacy contact update failed:", {
          status: error.response?.status,
          data: error.response?.data
        });
      }

      toast.error(error.response?.data?.message || "Failed to update legacy contact. Please try again.");
    }
  };

  const fetchReferrals = async () => {
    setReferralsLoading(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(`${apiConfig.apiBaseURL}/my-referrals`, {
        headers: {
          "Content-Type": "application/json",
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });

      if (response.data) {
        // Map API response to match component interface
        const referralsData = response.data.data || [];
        const mappedReferrals = referralsData.map((referral: any) => ({
          id: referral.id || referral.email,
          name: referral.fullName || "",
          email: referral.email || "",
          joinedDate: referral.joinedDate || "",
          referredOn: referral.referredDate || "",
          reward: referral.reward || "Pending",
        }));

        setReferrals(mappedReferrals);
        setReferralCode(response.data.referralCode || userProfile.myReferralCode || "");
      }
    } catch (error) {
      console.error("Error fetching referrals:", error);
      toast.error("Failed to load referrals. Please try again.");
    } finally {
      setReferralsLoading(false);
    }
  };

  const handleSendInvites = async (emails: string[]) => {
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/send-referral-email`,
        { emails },
        {
          headers: {
            "Content-Type": "application/json",
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
        }
      );

      if (response.data) {
        toast.success("Referral invitations sent successfully!");
        // Optionally refresh referrals list
        fetchReferrals();
      }
    } catch (error) {
      console.error("Error sending referral invitations:", error);
      toast.error(error.response?.data?.message || "Failed to send invitations. Please try again.");
      throw error;
    }
  };

  const handlePaymentHistoryPageChange = (page: number) => {
    if (page >= 1 && page <= paymentPagination.totalPages && page !== paymentPagination.currentPage) {
      fetchTransactionHistory(page);
    }
  };

  if (loading) {
    return (
      <>
        <Header />

        <div className="profile-correct">
          <Container>
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "400px" }}
            >
              <GiftLoader
                text="Loading your profile data..."
                size="large"
                showText={true}
              />
            </div>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />

      <div className="profile-correct">
        <Container>
          <Row className="profile-row">
            {/* LEFT SIDE - INDEPENDENT PROFILE SECTION */}
            <Col lg={4} md={12} className="profile-col">
              <div className="profile-left-section">
                {/* Profile Photo Upload */}
                <ProfilePhotoUpload
                  userProfile={userProfile}
                  onProfileUpdate={handleProfilePhotoUpdate}
                  getInitials={getInitials}
                />

                {/* Profile Contact Info */}
                <ProfileContactInfo
                  userProfile={userProfile}
                  onEditProfile={handleEditProfile}
                />

                {/* Invite Friends Button */}
                <Button
                  className="invite-friends-btn"
                  onClick={() => setShowReferFriendsModal(true)}
                >
                  Invite Friends
                </Button>
              </div>
            </Col>

            {/* RIGHT SIDE - TABBED CONTENT */}
            <Col lg={8} md={12} className="profile-col">
              <div className="profile-right-section">
                <Tab.Container activeKey={activeTab} onSelect={(key) => key && setActiveTab(key)}>
                  {/* Tab Navigation */}
                  <Nav variant="tabs" className="profile-tabs">
                    <Nav.Item>
                      <Nav.Link eventKey="subscription-legacy">
                        Subscription & Legacy Contact Information
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="payment-history">
                        Payment History
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="my-referrals">
                        My Referrals
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  {/* Tab Content */}
                  <Tab.Content className="tab-content">
                    <Tab.Pane eventKey="subscription-legacy">
                      <div className="subscription-legacy-content">
                        {/* Subscription Information */}
                        <SubscriptionSection subscriptionInfo={subscriptionInfo} />

                        {/* Legacy Contacts */}
                        <LegacyContactsSection
                          legacyContacts={legacyContacts}
                          onEditLegacyContact={handleEditLegacyContact}
                        />

                        <div className="legacy-contact-section">
                          <div className="legacy-contact-header">
                            <div className="legacy-contact-actions"></div>
                          </div>
                        </div>
                      </div>
                    </Tab.Pane>

                    <Tab.Pane eventKey="payment-history">
                      {paymentHistoryLoading ? (
                        <GiftLoader
                          text="Loading your payment history..."
                          size="medium"
                          showText={true}
                        />
                      ) : (
                        <PaymentHistory
                          paymentHistory={paymentHistory}
                          pagination={paymentPagination}
                          onPageChange={handlePaymentHistoryPageChange}
                        />
                      )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="my-referrals">
                      {referralsLoading ? (
                        <GiftLoader
                          text="Loading your referrals..."
                          size="medium"
                          showText={true}
                        />
                      ) : (
                        <MyReferrals
                          referrals={referrals}
                          onInviteFriends={() => setShowReferFriendsModal(true)}
                        />
                      )}
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Footer />

      {/* Modals */}
      <EditProfileModal
        show={showEditProfileModal}
        onHide={() => setShowEditProfileModal(false)}
        userProfile={userProfile}
        onUpdate={handleProfileUpdate}
      />

      <EditLegacyContactModal
        show={showEditLegacyModal}
        onHide={() => {
          setShowEditLegacyModal(false);
          setSelectedLegacyContact(null);
        }}
        contact={selectedLegacyContact}
        onUpdate={handleLegacyContactUpdate}
      />

      <ReferFriendsModal
        show={showReferFriendsModal}
        onHide={() => setShowReferFriendsModal(false)}
        referralCode={userProfile.myReferralCode}
        onSendInvites={handleSendInvites}
      />
    </>
  );
};

export default Profile;